/**
 * Transport and cache for the fitment payloads, shared by the finder and the product-page check.
 *
 * The two widgets share no other runtime, on purpose. This is the exception because they must agree
 * about exactly one thing - how a payload is fetched, cached, and recovered from - and two copies of
 * that logic would drift on the part where drift is most expensive.
 *
 * The theme no longer ships the data. It is fetched from the portal and kept in localStorage, which
 * is the entire reason a shopper does not wait on the network: a returning visitor renders from
 * their own copy and revalidation happens behind them. It is also the entire outage story. There is
 * no committed fallback asset, so when the portal is unreachable the cached copy is all there is -
 * hence `load()` returning stale data rather than failing, and only a first-ever visitor seeing the
 * "call us" card.
 */
(function () {
  if (window.WRLFitStore) return;

  var PREFIX = 'wrl.fit.';
  /** How old a cached copy may be before we revalidate behind the render. */
  var REVALIDATE_AFTER_MS = 60 * 60 * 1000;
  /**
   * A hung dyno must not leave the comboboxes reading "Loading..." forever. Eight seconds is longer
   * than any healthy response and shorter than a shopper's patience.
   */
  var TIMEOUT_MS = 8000;

  /**
   * Every localStorage access is wrapped, including reading the property itself: in a Safari private
   * window, or with site data blocked, `window.localStorage` *throws on access* rather than
   * returning null. Guarding only the calls is the version of this bug people ship.
   */
  function storage() {
    try {
      return window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function read(key) {
    var store = storage();
    if (!store) return null;
    var value;
    try {
      value = store.getItem(PREFIX + key);
    } catch (e) {
      return null;
    }
    if (!value) return null;

    // "<epochMs>\n<contentHash>\n<body>" - one item, so a quota failure can never leave metadata
    // pointing at a body that was not written. Parsed by hand rather than with JSON.parse on a
    // wrapper object, which would mean escaping a 223 KB string into another string.
    var firstBreak = value.indexOf('\n');
    var secondBreak = value.indexOf('\n', firstBreak + 1);
    if (firstBreak === -1 || secondBreak === -1) {
      drop(key);
      return null;
    }

    var savedAt = Number(value.slice(0, firstBreak));
    var body = value.slice(secondBreak + 1);
    if (!savedAt || !body) {
      drop(key);
      return null;
    }

    try {
      return {
        savedAt: savedAt,
        contentHash: value.slice(firstBreak + 1, secondBreak),
        parsed: JSON.parse(body),
      };
    } catch (e) {
      // A truncated or hand-edited copy. Dropping it turns a permanently broken widget into one
      // slow load.
      drop(key);
      return null;
    }
  }

  function drop(key) {
    var store = storage();
    if (!store) return;
    try {
      store.removeItem(PREFIX + key);
    } catch (e) {
      /* nothing to do */
    }
  }

  function write(key, body, contentHash) {
    var store = storage();
    if (!store) return false;
    var value = Date.now() + '\n' + (contentHash || '') + '\n' + body;

    try {
      store.setItem(PREFIX + key, value);
      return true;
    } catch (e) {
      // Out of room. Drop our own keys and try once more; if it still fails, carry on in memory for
      // this page load - a finder with no cache is correct, just not outage-proof.
      dropOurs();
      try {
        store.setItem(PREFIX + key, value);
        return true;
      } catch (e2) {
        return false;
      }
    }
  }

  function ourKeys() {
    var store = storage();
    if (!store) return [];
    var keys = [];
    try {
      for (var i = 0; i < store.length; i++) {
        var k = store.key(i);
        if (k && k.indexOf(PREFIX) === 0) keys.push(k);
      }
    } catch (e) {
      return [];
    }
    return keys;
  }

  function dropOurs() {
    var store = storage();
    if (!store) return;
    ourKeys().forEach(function (k) {
      try {
        store.removeItem(k);
      } catch (e) {
        /* nothing to do */
      }
    });
  }

  /**
   * Clears superseded versions of one payload family - "snapshot" or "pdp".
   *
   * localStorage is UTF-16, so the two payloads cost roughly 600 KB of a 5 MB per-origin budget that
   * is shared with Shopify's own scripts and every installed app. Leaving a previous version's copies
   * behind after a bump is how you cause a quota error in somebody else's code.
   *
   * Scoped to a family rather than taking a keep-list of every key, because a caller that names
   * another widget's key becomes the authority on a version constant it does not own: bump the PDP
   * schema in wrl-fit-check.js alone and a stale keep-list in the finder would delete the fresh PDP
   * cache on every boot, refetching ~73 KB forever with nothing to surface the mistake.
   */
  function evictOtherVersions(family, currentKey) {
    var store = storage();
    if (!store) return;
    var familyPrefix = PREFIX + family + '.v';
    ourKeys().forEach(function (k) {
      if (k.indexOf(familyPrefix) !== 0) return;
      if (k === PREFIX + currentKey) return;
      try {
        store.removeItem(k);
      } catch (e) {
        /* nothing to do */
      }
    });
  }

  /**
   * Fetches a payload.
   *
   * No custom request headers, deliberately. `If-None-Match` is not a CORS-safelisted request
   * header, so setting it would force a preflight on every load; and `ETag` is not a safelisted
   * response header, so script cannot read it cross-origin anyway. `cache: 'no-cache'` hands the
   * job to the browser's own HTTP cache, which revalidates with the validators it stored - not
   * subject to CORS at all. The 304s therefore come for free, and freshness bookkeeping here uses
   * the `contentHash` inside the payload, which is always readable.
   */
  function fetchPayload(url) {
    var options = { credentials: 'omit', cache: 'no-cache' };
    if (window.AbortSignal && AbortSignal.timeout) {
      options.signal = AbortSignal.timeout(TIMEOUT_MS);
    }

    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error('fitment feed returned ' + res.status);
      return res.text().then(function (body) {
        return { body: body, parsed: JSON.parse(body) };
      });
    });
  }

  var pending = {};

  /**
   * When a payload last failed to load, per key.
   *
   * Defence in depth, and it was earned: a widget that re-armed its IntersectionObserver after a
   * failure re-entered itself immediately and fetched a 404 hundreds of times in one page view.
   * That bug is fixed in wrl-fit.js, but the fix lives in the caller, and the portal should not
   * depend on every caller being careful. Inside the cooldown a load rejects without touching the
   * network, so the worst a runaway loop can do is spin locally.
   */
  var failedAt = {};
  var FAILURE_COOLDOWN_MS = 30000;

  /**
   * Resolves with `{ payload, stale, fromCache }` - always with something usable, or rejects.
   *
   * A fresh cached copy short-circuits entirely. A stale one is returned immediately and refreshed
   * behind the caller, which is told about the new payload through `onRefresh` so it can decide
   * whether redrawing is appropriate. A failed refresh is not an error: the stale copy stands.
   */
  function load(options) {
    var key = options.key;
    var url = options.url;
    var expectVersion = options.version;
    var onRefresh = options.onRefresh;

    var cached = read(key);
    if (cached && cached.parsed && cached.parsed.v === expectVersion) {
      if (Date.now() - cached.savedAt > REVALIDATE_AFTER_MS) {
        refresh(key, url, expectVersion, cached.contentHash, onRefresh);
      }
      return Promise.resolve({ payload: cached.parsed, fromCache: true });
    }
    if (cached) drop(key);

    var since = Date.now() - (failedAt[key] || 0);
    if (failedAt[key] && since < FAILURE_COOLDOWN_MS) {
      return Promise.reject(
        new Error("fitment feed failed " + Math.round(since / 1000) + "s ago; cooling down")
      );
    }

    // One request per payload per page, however many widgets are mounted.
    if (!pending[key]) {
      pending[key] = fetchPayload(url)
        .then(function (result) {
          if (result.parsed.v !== expectVersion) {
            throw new Error('unknown fitment schema v' + result.parsed.v);
          }
          write(key, result.body, result.parsed.contentHash);
          delete failedAt[key];
          return { payload: result.parsed, fromCache: false };
        })
        .catch(function (err) {
          // Never cache the rejection. One blip would otherwise disable the widget for the rest of
          // the page load, for every instance on the page.
          delete pending[key];
          // The cooldown above is what keeps "retryable" from meaning "unbounded".
          failedAt[key] = Date.now();
          throw err;
        });
    }
    return pending[key];
  }

  function refresh(key, url, expectVersion, previousHash, onRefresh) {
    if (pending['refresh:' + key]) return;
    if (failedAt[key] && Date.now() - failedAt[key] < FAILURE_COOLDOWN_MS) return;
    pending['refresh:' + key] = true;

    fetchPayload(url)
      .then(function (result) {
        if (result.parsed.v !== expectVersion) return;
        write(key, result.body, result.parsed.contentHash);
        var hash = String(result.parsed.contentHash || '');
        if (onRefresh && hash !== String(previousHash || '')) onRefresh(result.parsed);
      })
      .catch(function (err) {
        // The whole outage story, in one place: the shopper is already looking at working data, so a
        // failed revalidation changes nothing. With no committed fallback asset this is what keeps
        // the finder alive while the portal is unreachable.
        failedAt[key] = Date.now();
        if (window.console && console.debug) {
          console.debug('[wrl-fit] revalidation failed; keeping the cached snapshot', err);
        }
      })
      .then(function () {
        delete pending['refresh:' + key];
      });
  }

  /**
   * The shopper's tractor, remembered across pages.
   *
   * This is the second thing the two widgets must agree about, and it belongs here for the same
   * reason the payload cache does: the finder writes it, the product page reads it, and a second
   * copy of the key name and the staleness rule would drift. It is deliberately tiny and holds no
   * product data - just enough for the product page to say which tractor it is talking about.
   *
   * Thirty days, because a shopper who bought a tractor is still driving the same tractor next
   * month, and a stale name under a "verified fit" badge is worse than no badge at all. The model
   * key is re-checked against the live index before anything is claimed, so an expired or renamed
   * model degrades to silence rather than to a wrong claim.
   */
  var TRACTOR_KEY = 'tractor.v1';
  var TRACTOR_TTL_MS = 30 * 24 * 60 * 60 * 1000;

  function saveTractor(sel) {
    if (!sel || !sel.modelKey || !sel.modelName) return false;
    // Names only, never a whole selection object: this is written on every narrowing step.
    return write(TRACTOR_KEY, JSON.stringify({
      modelKey: String(sel.modelKey),
      modelName: String(sel.modelName),
      loaderKey: sel.loaderKey ? String(sel.loaderKey) : null,
      loaderName: sel.loaderName ? String(sel.loaderName) : null,
    }), '');
  }

  function tractor() {
    var entry = read(TRACTOR_KEY);
    if (!entry) return null;
    if (Date.now() - entry.savedAt > TRACTOR_TTL_MS) {
      drop(TRACTOR_KEY);
      return null;
    }
    var value = entry.parsed;
    if (!value || !value.modelKey || !value.modelName) return null;
    return value;
  }

  function forgetTractor() {
    drop(TRACTOR_KEY);
  }

  window.WRLFitStore = {
    load: load,
    evictOtherVersions: evictOtherVersions,
    read: read,
    drop: drop,
    saveTractor: saveTractor,
    tractor: tractor,
    forgetTractor: forgetTractor,
    REVALIDATE_AFTER_MS: REVALIDATE_AFTER_MS,
  };
})();
